import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  confirmStrategy,
  forkStrategy,
  getCurrentStrategy,
  getStrategyDetail,
  listStrategyHistory,
  seedStrategy,
} from '@/lib/strategyApi'

const STRATEGY_KEY = ['strategy'] as const

export function useCurrentStrategy() {
  return useQuery({
    queryKey: [...STRATEGY_KEY, 'current'],
    queryFn: getCurrentStrategy,
    retry: (failureCount, error: unknown) => {
      // 404 (keine aktive Strategy) ist erwartbar — nicht retryen
      const status = (error as { response?: { status?: number } } | undefined)?.response?.status
      if (status === 404) return false
      return failureCount < 2
    },
    staleTime: 60_000,
  })
}

export function useStrategyDetail(id: number | null) {
  return useQuery({
    queryKey: [...STRATEGY_KEY, 'detail', id],
    queryFn: () => getStrategyDetail(id!),
    enabled: id !== null,
    staleTime: 60_000,
  })
}

export function useStrategyHistory() {
  return useQuery({
    queryKey: [...STRATEGY_KEY, 'history'],
    queryFn: listStrategyHistory,
    staleTime: 30_000,
  })
}

export function useSeedStrategy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: seedStrategy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGY_KEY })
    },
  })
}

export function useConfirmStrategy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: confirmStrategy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGY_KEY })
    },
  })
}

export function useForkStrategy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) => forkStrategy(id, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STRATEGY_KEY })
    },
  })
}
